import React, { useMemo } from "react";
import { Grid3x3 } from "lucide-react";

// Light -> medium teal ramp (kept light enough for dark text to stay AA-legible)
function shade(t) {
  const from = [233, 246, 244]; // #E9F6F4
  const to = [102, 198, 188];   // #66C6BC
  const c = from.map((f, i) => Math.round(f + (to[i] - f) * t));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

export const SectorHeatmap = ({ stocks = [], activeSector = "", onSelect }) => {
  const sectors = useMemo(() => {
    const map = {};
    for (const s of stocks) {
      const sec = s.sector || "Other";
      if (!map[sec]) map[sec] = { sector: sec, total: 0, qualifying: 0 };
      map[sec].total += 1;
      if (s.qualifies) map[sec].qualifying += 1;
    }
    return Object.values(map).sort(
      (a, b) => b.qualifying - a.qualifying || b.total - a.total
    );
  }, [stocks]);

  const maxQual = useMemo(
    () => Math.max(1, ...sectors.map((s) => s.qualifying)),
    [sectors]
  );

  if (!sectors.length) return null;

  return (
    <div
      className="bg-white border border-[#D2E0E6] rounded-lg shadow-sm p-5 mb-6"
      data-testid="sector-heatmap"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-[#0B2830]">
          <Grid3x3 className="w-4 h-4 text-[#0D9488]" />
          <span className="text-sm font-semibold">Sector heatmap</span>
          <span className="text-xs text-[#7C949C]">qualifying names by sector &middot; current filters</span>
        </div>
        {activeSector && (
          <button
            data-testid="heatmap-clear"
            onClick={() => onSelect && onSelect(activeSector)}
            className="text-xs text-[#0284C7] hover:underline"
          >
            Clear sector filter
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {sectors.map((s) => {
          const t = s.qualifying / maxQual;
          const isActive = activeSector === s.sector;
          return (
            <button
              key={s.sector}
              data-testid={`heatmap-cell-${slug(s.sector)}`}
              onClick={() => onSelect && onSelect(s.sector)}
              title={`${s.sector}: ${s.qualifying} qualify of ${s.total} screened`}
              className={`relative overflow-hidden text-left rounded-md px-3 py-2.5 border transition-all ${
                isActive
                  ? "border-[#0284C7] ring-2 ring-[#0284C7]/30"
                  : "border-[#E6EEF1] hover:border-[#0D9488]/60"
              }`}
              style={{
                background: shade(0.06 + t * 0.94),
                boxShadow: `inset 4px 0 0 rgba(13,148,136,${(0.3 + t * 0.7).toFixed(2)})`,
              }}
            >
              <div className="text-sm font-semibold truncate text-[#0B2830]">
                {s.sector}
              </div>
              <div className="mt-0.5 text-xs text-[#1F3D42]">
                <span className="font-bold text-base text-[#0B2830]">{s.qualifying}</span>
                <span> / {s.total} qualify</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SectorHeatmap;
